import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch } from 'store/hooks';
import { getPatientDetails } from 'store/slices/userSlice';
import { AuthContext } from 'utils/AuthContext';

/** MM-1132: age from which a patient is treated as an adult. */
const ADULT_AGE = 18;

/**
 * MM-1132: tells whether the open patient is 18 or older, from the date of birth on their profile
 * (MM/DD/YYYY). Re-checks on every child switch.
 *
 * @returns true when 18 or older, false when younger or the date of birth is missing or invalid,
 *          null while still loading
 */
export const usePatientIsAdult = (): boolean | null => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const patientId = user?.patientId;
  /** Result of the age check; null until the profile has loaded. */
  const [isAdult, setIsAdult] = useState<boolean | null>(null);

  useEffect(() => {
    if (!patientId) {
      return;
    }
    setIsAdult(null);
    dispatch(getPatientDetails(patientId))
      .unwrap()
      .then((response: any) => {
        const dob = dayjs(response?.data?.dateOfBirth);
        setIsAdult(response?.status === 200 && dob.isValid() && dayjs().diff(dob, 'year') >= ADULT_AGE);
      })
      .catch(() => setIsAdult(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  return isAdult;
};
